/**
 * MessageList Component
 * =====================
 * 
 * Renders the scrollable message list.
 * Handles: message grouping, scroll to bottom, message items.
 * 
 * Pure presentation - all data and handlers from props.
 */
import React, { useRef, useEffect } from 'react';
import MessageItem from './MessageItem';

export function MessageList({
  messages,
  currentUserId,
  matchProfile,
  loading,
  loadError,
  
  // Reaction handlers
  onReactionPickerOpen,
  reactionUI,
  onReactionToggle,
  onReactionPickerClose,
  
  // Auto-scroll
  shouldAutoScroll = true,
}) {
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  
  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (shouldAutoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, shouldAutoScroll]);
  
  if (loading) {
    return (
      <div 
        className="flex-1 flex items-center justify-center"
        data-testid="chat-loading"
      >
        <div className="text-gray-400">Loading messages...</div>
      </div>
    );
  }
  
  if (loadError) {
    return (
      <div 
        className="flex-1 flex items-center justify-center"
        data-testid="chat-error"
      >
        <div className="text-center p-4">
          <div className="text-red-500 mb-2">⚠️</div>
          <div className="text-gray-600 text-sm">{loadError}</div>
        </div>
      </div>
    );
  }
  
  if (!messages || messages.length === 0) {
    return (
      <div 
        className="flex-1 flex items-center justify-center"
        data-testid="chat-empty"
      >
        <div className="text-center text-gray-400">
          <p className="text-lg mb-2">💬</p>
          <p>Start the conversation!</p>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      ref={containerRef}
      className="flex-1 overflow-y-auto overflow-x-hidden px-2 sm:px-4 py-4"
      data-testid="message-list"
    >
      {messages.map((message, index) => (
        <MessageItem
          key={message.id || `msg-${index}`}
          message={message}
          isOwnMessage={message.sender_id === currentUserId}
          matchProfile={matchProfile}
          onReactionPickerOpen={onReactionPickerOpen}
          reactionUI={reactionUI}
          onReactionToggle={onReactionToggle}
          onReactionPickerClose={onReactionPickerClose}
        />
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
}

export default MessageList;
